import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Facebook-style Messages icon for the top header.
 * Round circular button + red unread badge + dropdown with recent threads.
 */
export default function MessagesBubble() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let alive = true;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/dm/unread-count`);
        if (alive) setUnread(r.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    axios
      .get(`${API}/dm/threads`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.threads || []);
        setThreads(list.slice(0, 6));
      })
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="messages-bubble-btn"
        aria-label="Messages"
        className="relative w-10 h-10 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/85 hover:text-[#00AFFF] transition-all"
      >
        <MessageCircle size={18} />
        {unread > 0 && (
          <span
            data-testid="messages-bubble-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-[#FF3B6B] text-white text-[10px] font-display font-black border-2 border-[#020B1F] shadow-[0_0_8px_rgba(255,59,107,0.6)]"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="messages-bubble-panel"
          className="absolute right-0 mt-2 w-80 sm:w-96 glass-strong rounded-2xl border border-[#00AFFF]/25 shadow-2xl z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h3 className="font-display font-black text-white text-base">Messages</h3>
            <Link
              to="/messages"
              onClick={() => setOpen(false)}
              data-testid="messages-bubble-see-all"
              className="text-[10px] uppercase tracking-widest text-[#00AFFF] hover:text-[#00C2FF] font-bold"
            >
              See all
            </Link>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-white/45 text-sm">Loading…</div>
            ) : threads.length === 0 ? (
              <div className="p-6 text-center text-white/55 text-sm italic">
                No messages yet — start a conversation.
              </div>
            ) : (
              <ul>
                {threads.map((t) => {
                  const other = t.other_user || t.peer || {};
                  const isUnread = (t.unread_count || 0) > 0;
                  return (
                    <li key={t.id || t.thread_id || other.id}>
                      <Link
                        to={`/messages/${t.id || t.thread_id || other.id}`}
                        onClick={() => setOpen(false)}
                        data-testid={`messages-bubble-thread-${t.id || other.id}`}
                        className={`flex items-center gap-3 p-3 hover:bg-white/[0.05] transition border-b border-white/5 ${
                          isUnread ? "bg-[#00AFFF]/[0.05]" : ""
                        }`}
                      >
                        {other.avatar ? (
                          <img src={other.avatar} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-white/15 flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 grid place-items-center rounded-full bg-gradient-to-br from-[#00AFFF] to-[#0044FF] font-display font-black text-white text-sm flex-shrink-0">
                            {(other.username || "?").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-display font-bold text-sm truncate ${isUnread ? "text-white" : "text-white/75"}`}>
                              {other.display_name || other.username || "Unknown"}
                            </span>
                            {isUnread ? (
                              <span className="w-2 h-2 rounded-full bg-[#00AFFF] flex-shrink-0" aria-label="unread" />
                            ) : null}
                          </div>
                          <div className={`text-[12px] truncate ${isUnread ? "text-white/85 font-medium" : "text-white/45"}`}>
                            {t.last_message?.body || t.preview || "No messages yet"}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <Link
            to="/messages"
            onClick={() => setOpen(false)}
            data-testid="messages-bubble-footer-cta"
            className="block text-center py-2.5 text-[11px] uppercase tracking-widest text-[#00AFFF] hover:bg-white/5 font-bold border-t border-white/10"
          >
            Open Messages →
          </Link>
        </div>
      )}
    </div>
  );
}
